import {
  KNOWLEDGE_CATALOG_DOWNLOAD_END,
  KNOWLEDGE_CATALOG_DOWNLOAD_START,
  KNOWLEDGE_CATALOG_REFETCH_REQUESTED,
  KNOWLEDGE_DASHBOARD_UPDATE_CONTENT,
  KNOWLEDGE_CATEGORIES_FETCH_START,
  KNOWLEDGE_CATEGORIES_FETCH_END,
  KNOWLEDGE_CATEGORIES_REFETCH_REQUESTED,
  KNOWLEDGE_UPDATE_CATEGORIES,
  KNOWLEDGE_UPDATE_CONTENT,
  KNOWLEDGE_UPDATE_KEYWORDS,
  KNOWLEDGE_USE_CACHE,
  KNOWLEDGE_TRIGGER_UPDATE,
} from '.';
import knowledgeServiceApi from '../../api/knowledge.service.api';
import {
  KNOWLEDGE_CATALOG_PAGE_RETRY_DELAY_MS,
  KNOWLEDGE_CATALOG_PAGE_SIZE,
} from '../../constants/knowledge.constants';
import { isConnected } from '../selectors/network.selector';
import { needsKnowledgeCatalog } from '../selectors/features.selector';
import { normalizeRevisionLog } from '../../utils/knowledge.utils';
import { queryKeys } from '../../api/query-client-registry';
import { invalidateQueryCache } from './query-cache.actions';

// Only has to differ from the previous download's id within one store lifetime.
const newRequestId = () => Math.random().toString(36).slice(2);

// The boot fetch. A client that reads item rows on demand loads the tree only;
// one that still searches client-side (see `needsKnowledgeCatalog`) downloads
// every item row as well.
//
// Single-flight: login, token refresh, reconnect and content changes all fire
// this during boot, and every call was a full catalog sweep. The running
// download's id is kept in the knowledge slice, so a wipe or logout clears it
// like any other state and the stale result is then ignored.
//
// `refetchIfRunning`: a content change may postdate pages already fetched, so
// those callers book one more run after the current one. Boot, auth and
// reconnect triggers just join the running download.
export const getKnowledgeContent =
  ({ refetchIfRunning = false } = {}) =>
  async (dispatch, getState) => {
    if (!needsKnowledgeCatalog(getState())) {
      return dispatch(getKnowledgeCategories({ refetchIfRunning }));
    }

    if (!isConnected(getState())) {
      return dispatch({ type: KNOWLEDGE_USE_CACHE });
    }

    if (getState().knowledge?.catalogRequestId) {
      return refetchIfRunning ? dispatch({ type: KNOWLEDGE_CATALOG_REFETCH_REQUESTED }) : undefined;
    }

    const requestId = newRequestId();
    dispatch({ type: KNOWLEDGE_CATALOG_DOWNLOAD_START, payload: { requestId } });
    let stillCurrent = false;
    try {
      await downloadCatalog(dispatch, getState, requestId);
    } finally {
      stillCurrent = getState().knowledge?.catalogRequestId === requestId;
      dispatch({ type: KNOWLEDGE_CATALOG_DOWNLOAD_END, payload: { requestId } });
    }

    // A download wiped mid-flight must not act on a booking that belongs to
    // the session that replaced it.
    if (stillCurrent && getState().knowledge?.catalogRefetchPending) {
      await dispatch(getKnowledgeContent());
    }
  };

const indexById = list => Object.fromEntries(list.map(entry => [entry.id, entry]));

/**
 * Refreshes the category tree only, leaving the item rows as they are.
 * Sets `isReady` / `lastUpdated` like the full catalog download does.
 * Single-flight like the catalog download: the boot triggers join a fetch in
 * flight, and a content change (`refetchIfRunning`) books one more fetch after
 * it, since the running one may predate the change. Latest wins: an older
 * response that lands after a newer one is dropped, and so is one that lands
 * after a wipe. While a full download runs it books one more download instead.
 */
export const getKnowledgeCategories =
  ({ refetchIfRunning = false } = {}) =>
  async (dispatch, getState) => {
    if (!isConnected(getState())) {
      return dispatch({ type: KNOWLEDGE_USE_CACHE });
    }

    if (getState().knowledge?.catalogRequestId) {
      return dispatch({ type: KNOWLEDGE_CATALOG_REFETCH_REQUESTED });
    }

    if (getState().knowledge?.categoriesRequestId) {
      return refetchIfRunning
        ? dispatch({ type: KNOWLEDGE_CATEGORIES_REFETCH_REQUESTED })
        : undefined;
    }

    const requestId = newRequestId();
    dispatch({ type: KNOWLEDGE_CATEGORIES_FETCH_START, payload: { requestId } });
    let stillCurrent = false;

    try {
      const res = await retryOnce(() => knowledgeServiceApi.getCategories());
      if (getState().knowledge?.categoriesRequestId !== requestId) return;
      // Known here, not after the dispatch: a tree that lands clears the id.
      stillCurrent = true;

      if (res.error) {
        return dispatch({ type: KNOWLEDGE_USE_CACHE });
      }

      dispatch({
        type: KNOWLEDGE_UPDATE_CATEGORIES,
        payload: { categories: res.data, categoriesById: indexById(res.data), requestId },
      });
    } finally {
      dispatch({ type: KNOWLEDGE_CATEGORIES_FETCH_END, payload: { requestId } });
    }

    if (stillCurrent && getState().knowledge?.categoriesRefetchPending) {
      await dispatch(getKnowledgeCategories());
    }
  };

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const retryDelayMs = () => {
  const { min, max } = KNOWLEDGE_CATALOG_PAGE_RETRY_DELAY_MS;
  return min + Math.random() * (max - min);
};

// Only a request that got no response at all (network error, timeout). A 4xx
// fails the same way again, and the Executer already retries every 5xx three
// times, so retrying those here would only multiply the load on a failing backend.
const isRetryable = error => error?.response === undefined;

const retryOnce = async request => {
  const res = await request();
  if (!res.error || !isRetryable(res.error)) return res;
  await wait(retryDelayMs());
  return request();
};

const fetchCatalogPage = params => retryOnce(() => knowledgeServiceApi.getItemsByPage(params));

const downloadCatalog = async (dispatch, getState, requestId) => {
  const isCurrent = () => getState().knowledge?.catalogRequestId === requestId;
  const pageSize = KNOWLEDGE_CATALOG_PAGE_SIZE;

  // `total` is opt-in and counted once: only the first request asks for it.
  let [itemsRes, categoriesRes] = await Promise.all([
    fetchCatalogPage({ pageSize, includeTotal: true }),
    retryOnce(() => knowledgeServiceApi.getCategories()),
  ]);

  if (itemsRes.error || categoriesRes.error) {
    if (isCurrent()) dispatch({ type: KNOWLEDGE_USE_CACHE });
    return;
  }

  let items = [...(itemsRes.data.items ?? [])];
  // A backend without cursor paging answers with no nextCursor at all; walk the
  // remaining pages by number so a client deployed first still gets everything.
  const usesCursor = 'nextCursor' in itemsRes.data;
  let nextCursor = usesCursor ? (itemsRes.data.nextCursor ?? null) : null;
  let nextPage = !usesCursor && itemsRes.data.total > items.length ? 2 : null;
  const lastPage = Math.ceil((itemsRes.data.total ?? 0) / pageSize);

  // Cursor pages are sequential by nature: one request in flight per device.
  while (nextCursor || nextPage) {
    // Wiped, logged out or org-switched mid-flight: stop spending requests on it.
    if (!isCurrent()) return;

    const pageRes = await fetchCatalogPage(
      nextCursor ? { pageSize, after: nextCursor } : { pageSize, page: nextPage },
    );

    if (pageRes.error) {
      if (isCurrent()) dispatch({ type: KNOWLEDGE_USE_CACHE });
      return;
    }

    items.push(...(pageRes.data?.items ?? []));
    if (nextCursor) {
      const cursor = pageRes.data?.nextCursor ?? null;
      // A cursor that does not advance would loop forever; treat it as the end.
      nextCursor = cursor === nextCursor ? null : cursor;
    } else {
      nextPage = nextPage < lastPage ? nextPage + 1 : null;
    }
  }

  items = items.map(normalizeRevisionLog);

  if (!isCurrent()) return;

  dispatch({
    type: KNOWLEDGE_UPDATE_CONTENT,
    payload: {
      items,
      itemsById: indexById(items),
      categories: categoriesRes.data,
      categoriesById: indexById(categoriesRes.data),
      requestId,
    },
  });
};

export const addKeywordsToKnowledgeItem =
  ({ itemId, keywords }) =>
  async (dispatch, getState) => {
    const state = getState();

    if (!isConnected(state)) {
      return dispatch({
        type: KNOWLEDGE_USE_CACHE,
      });
    }

    const res = await knowledgeServiceApi.updateItemKeywords({ id: itemId, keywords });

    if (res.error) {
      return dispatch({
        type: KNOWLEDGE_USE_CACHE,
      });
    }

    dispatch({
      type: KNOWLEDGE_UPDATE_KEYWORDS,
      payload: {
        id: itemId,
        keywords,
      },
    });
    dispatch(invalidateQueryCache(queryKeys.knowledgeItems));
  };

export const getKnowledgeDashboardContent = () => async (dispatch, getState) => {
  let state = getState();

  if (!isConnected(state)) {
    return dispatch({
      type: KNOWLEDGE_USE_CACHE,
    });
  }

  let [itemsOpenEventsForOrganizationRes, itemsOpenEventsForUserRes] = await Promise.all([
    knowledgeServiceApi.getKnowledgeItemsOpenEventsForOrganization(),
    knowledgeServiceApi.getKnowledgeItemsOpenEventsForUser(),
  ]);

  if (itemsOpenEventsForOrganizationRes.error || itemsOpenEventsForUserRes.error) {
    return dispatch({
      type: KNOWLEDGE_USE_CACHE,
    });
  }

  dispatch({
    type: KNOWLEDGE_DASHBOARD_UPDATE_CONTENT,
    payload: {
      itemsOpenEventsForOrganization: itemsOpenEventsForOrganizationRes.data.items,
      itemsOpenEventsForUser: itemsOpenEventsForUserRes.data.items,
    },
  });
};

export const getFrequentlyUsedKnowledgeContent = () => async (dispatch, getState) => {
  const state = getState();

  if (!isConnected(state)) {
    return dispatch({
      type: KNOWLEDGE_USE_CACHE,
    });
  }

  const itemsOpenEventsForOrganizationRes =
    await knowledgeServiceApi.getKnowledgeItemsOpenEventsForOrganization();

  if (itemsOpenEventsForOrganizationRes.error) {
    return dispatch({
      type: KNOWLEDGE_USE_CACHE,
    });
  }

  dispatch({
    type: KNOWLEDGE_DASHBOARD_UPDATE_CONTENT,
    payload: {
      itemsOpenEventsForOrganization: itemsOpenEventsForOrganizationRes.data.items,
    },
  });
};

/**
 * Triggers a knowledge content update that can be used by any component
 * to refresh knowledge-related data throughout the system.
 *
 * @param {Object} options - Update options
 * @param {string} options.type - Type of update: 'refresh', 'update', 'add', 'remove'
 * @param {any} options.data - Data associated with the update
 */
export const triggerKnowledgeUpdate =
  (options = {}) =>
  dispatch => {
    dispatch({
      type: KNOWLEDGE_TRIGGER_UPDATE,
      payload: options,
    });
  };
